/* eslint-disable max-len, no-continue */
import userApi from '_api/user'
import orderApi from '_api/order'
import reportApi from '_api/report'
import { BUYER_NOT_IN_GROUP_CODE } from '_utils/errorCodes'
import { getUserInfo, removeUserToken } from '_utils/localData'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useToasts } from 'react-toast-notifications'

import {
  clearEventAndOrder,
  updateEventWhenOrder,
  updateTotalOrderCountWhenUpdateOrder,
} from '_redux/modules/event'
import {
  clearPastPurchasedMember,
  updateTotalOrderCountInGroupWhenOrder,
  updateTotalOrderCountInGroupWhenOrderWhenUpdateOrder,
} from '_redux/modules/group'
import { clearDataPurchaseDetailByEid, clearMyOrder } from '_redux/modules/order'

/**
 * Owns everything about actually placing/updating the order: the comment /
 * purchase-on-behalf / submit state, the success modal, and the address ->
 * proceedWithOrder wiring. Ported 1:1 from EventDetail#proceedWithOrder,
 * #onSubmit and the three handleAddress* callbacks.
 */
const useOrderSubmission = ({
  eid,
  oid,
  shareCode,
  sharedId,
  isGuestCheckout,
  guestFormData,
  isEventShop,
  edittingOrder,
  eventDetail,
  editOrderBookingAddress,
  payLater,
  initialComment,
  initTotalOrderCount,
  newOrderProducts,
  newOrderProductsIndividual,
  quantities,
  addressBook,
  history,
}) => {
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const userInfo = getUserInfo()

  const [comment, setComment] = useState('')
  const [error, setError] = useState('')
  const [isPurchase, setIsPurchase] = useState(false)
  const [phoneOrEmail, setPhoneOrEmail] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [orderNo, setOrderNo] = useState('')
  const [hasItemWithAckRequired, setHasItemWithAckRequired] = useState(false)

  const helpOrder = !!phoneOrEmail

  const commentSeededRef = useRef(false)
  useEffect(() => {
    if (commentSeededRef.current) return
    if (initialComment) {
      commentSeededRef.current = true
      setComment(initialComment)
    }
  }, [initialComment])

  useEffect(() => {
    setError('')
  }, [newOrderProducts])

  const userInGroup = userInfo?.groups?.find(
    (group) => group.gid === eventDetail?.group?.id && group.uid === userInfo?.id
  )

  const proceedWithOrder = async (addressOverride = null, commentOverride = null) => {
    try {
      let newOrder = {}
      setIsSubmitted(true)
      let newProducts = newOrderProducts.filter(
        (p) => {
          const apiProduct = eventDetail.products?.find((pApi) => pApi.id === p.id)
          const isTrack = apiProduct?.subItems?.some((s) => s.trackInventory) || false
          const totalSubQty = p.subItems?.reduce((acc, sub) => acc + parseInt(sub.quantity ?? sub.qty ?? 0, 10), 0) || 0
          return parseInt(p.quantity || 0, 10) > 0 ||
          (isTrack && totalSubQty > 0) ||
          (p.splitCasePurchase && p.splitCasePurchase.enabled && p.splitCasePurchase.orderCount > 0)
        }
      )

      if (!edittingOrder) {
        const details = {}
        let foundAckRequired = false

        for (let index = 0; index < newProducts.length; index++) {
          const product = newProducts[index]
          const arr = []
          const apiProduct = eventDetail.products?.find((pApi) => pApi.id === product.id)
          if (
            apiProduct &&
            apiProduct.splitCasePurchase &&
            apiProduct.splitCasePurchase.acknowledgementRequiredPayAfter === true
          ) {
            foundAckRequired = true
          }

          const isTrack = apiProduct?.subItems?.some((s) => s.trackInventory) || false
          const totalSubQty = product.subItems?.reduce((acc, sub) => acc + parseInt(sub.quantity ?? sub.qty ?? 0, 10), 0) || 0
          const effectiveQty = isTrack ? totalSubQty : parseInt(product.quantity || 0, 10)

          if (effectiveQty > 0) {
            arr.push({
              purchaseType: 'case',
              quantity: effectiveQty,
              subItems:
                product.subItems &&
                !!product.subItems.length &&
                product.subItems.map((s) => ({
                  type: s.type,
                  name: s.name,
                  price: s.price,
                  qty: parseInt(s.quantity ?? s.qty ?? 1, 10),
                })),
            })
          }
          if (product.splitCasePurchase && product?.splitCasePurchase?.enabled) {
            const splitQty = quantities[`${product.id}-${product.index}-split`] ?? 0
            if (splitQty > 0) {
              arr.push({
                purchaseType: 'item',
                quantity: splitQty,
              })
            }
          }

          if (arr.length > 0) details[product.id] = [...(details[product.id] || []), ...arr]
        }
        setHasItemWithAckRequired(foundAckRequired)
        if (!Object.keys(details).length) {
          setError('Nothing to Purchase')
          setIsSubmitted(false)
          return
        }
        if (helpOrder) {
          const resFindUser = await userApi.searchUser(phoneOrEmail)
          const { uid } = resFindUser?.msgResp
          if (uid) {
            newOrder = { ...newOrder, representedUserId: uid }
          } else {
            setError('User not found.')
            setIsSubmitted(false)
            return
          }
        }

        newOrder = {
          ...newOrder,
          eid,
          details,
          comment: commentOverride !== null ? commentOverride : comment,
        }
      }

      if (edittingOrder) {
        const details = {}
        newProducts = [...newProducts, ...newOrderProductsIndividual]
        let foundAckRequired = false
        for (let index = 0; index < newProducts.length; index++) {
          const product = newProducts[index]
          const apiProduct = eventDetail.products?.find((pApi) => pApi.id === product.id)
          if (
            apiProduct &&
            apiProduct.splitCasePurchase &&
            apiProduct.splitCasePurchase.acknowledgementRequiredPayAfter === true
          ) {
            foundAckRequired = true
            break
          }
        }
        setHasItemWithAckRequired(foundAckRequired)
        for (const product of newProducts) {
          if (product.purchaseType === 'item' && newProducts.some((p) => p.purchaseType === 'case' && p.id === product.id)) continue
          const arr = []
          const isCase = product.purchaseType !== 'item'
          const apiProduct = eventDetail.products?.find((pApi) => pApi.id === product.id)
          const isTrack = apiProduct?.subItems?.some((s) => s.trackInventory) || false
          const totalSubQty = product.subItems?.reduce((acc, sub) => acc + parseInt(sub.quantity ?? sub.qty ?? 0, 10), 0) || 0
          const effectiveQty = isTrack ? totalSubQty : parseInt(product.quantity || 0, 10)

          if (isCase && effectiveQty > 0) {
            arr.push({
              purchaseType: product.purchaseType || 'case',
              quantity: effectiveQty,
              subItems:
                product.subItems &&
                !!product.subItems.length &&
                product.subItems.map((s) => ({
                  type: s.type,
                  name: s.name,
                  price: s.price,
                  qty: parseInt(s.quantity ?? s.qty ?? 1, 10),
                })),
            })
          }

          if (quantities[`${product.id}-${product.index}-split`] > 0) {
            const splitQty = quantities[`${product.id}-${product.index}-split`] ?? 0
            if (splitQty > 0) {
              arr.push({
                purchaseType: 'item',
                quantity: splitQty,
              })
            }
          }

          if (!details[product.id]) details[product.id] = []
          arr.forEach((item) => {
            const existing = details[product.id].find(
              (d) =>
                d.purchaseType === item.purchaseType &&
                JSON.stringify(d.subItems || []) === JSON.stringify(item.subItems || [])
            )
            if (existing) {
              existing.quantity += item.quantity
            } else {
              details[product.id].push(item)
            }
          })
        }
        if (!Object.keys(details).length) {
          setError('Nothing to Purchase')
          setIsSubmitted(false)
          return
        }
        newOrder = {
          ...newOrder,
          eid,
          details,
          comment: commentOverride !== null ? commentOverride : comment,
        }
      }

      if (eventDetail.bookingAddressRequired) {
        const bookingAddressObj =
          addressOverride ||
          addressBook.selectedDeliveryAddress ||
          addressBook.userAddresses.find((addr) => addr.isDefault) ||
          addressBook.userAddresses[0]

        if (bookingAddressObj) {
          const addressStr = bookingAddressObj.address?.street || bookingAddressObj.address || ''
          const postalCode = bookingAddressObj.postalCode || ''
          const country = bookingAddressObj.country || 'Singapore'
          newOrder.bookingAddress = `${addressStr}, ${country}, ${postalCode}`
        } else if (edittingOrder && editOrderBookingAddress) {
          newOrder.bookingAddress = editOrderBookingAddress
        }

        newOrder.deliveryDate = bookingAddressObj.deliveryDate
        newOrder.consignee = bookingAddressObj.consignee || userInfo?.name
        newOrder.phone = bookingAddressObj.phone || userInfo?.phone || ''
      }

      if (edittingOrder) {
        dispatch(clearPastPurchasedMember())
        dispatch(clearDataPurchaseDetailByEid(newOrder?.eid))
        dispatch(clearEventAndOrder())
        try {
          const { msgResp } = await orderApi.updateOrder({ oid, data: newOrder })

          if (payLater) {
            if (isEventShop) {
              history.push(`/event/purchase-detail?eventShop=true&ref=${oid}`)
            } else {
              history.push(`/event/purchase-detail?ref=${oid}`)
            }
          } else {
            setShowModal(true)
          }

          setOrderNo(msgResp?.ref)
          setIsSubmitted(false)
          dispatch(clearMyOrder())
        } catch (err) {
          reportApi.report({ message: `[Event detail 03]${err.msgCode} - ${err.msgResp}` })
          setError(err.msgResp)
          setIsSubmitted(false)
        }

        dispatch(updateTotalOrderCountWhenUpdateOrder({ ...newOrder, initTotalOrderCount }))
        dispatch(
          updateTotalOrderCountInGroupWhenOrderWhenUpdateOrder({ ...newOrder, initTotalOrderCount })
        )
      } else {
        try {
          dispatch(clearPastPurchasedMember())
          dispatch(clearDataPurchaseDetailByEid(newOrder?.eid))
          dispatch(clearEventAndOrder())
          const res = await orderApi.createOrder(newOrder)
          dispatch(updateEventWhenOrder(newOrder))
          dispatch(updateTotalOrderCountInGroupWhenOrder(newOrder))
          if (res) {
            if (isGuestCheckout && guestFormData?.createAccount === false && guestFormData?.rememberInfo === false) {
              removeUserToken()
            }

            if (payLater) {
              if (isEventShop) {
                history.push(`/event/purchase-detail?eventShop=true&ref=${res?.msgResp?.ref}`)
              } else {
                history.push(`/event/${(userInfo?.id || guestFormData?.createAccount) ? 'purchase-detail' : 'purchase-detail-share'}?ref=${res?.msgResp?.ref}`)
              }
            } else {
              setShowModal(true)
            }
            setOrderNo(res?.msgResp?.ref)
            setIsSubmitted(false)
            if (!helpOrder) dispatch(clearMyOrder())
          }
        } catch (err) {
          setIsSubmitted(false)
          if (err?.msgCode === BUYER_NOT_IN_GROUP_CODE && (shareCode || sharedId)) {
            addToast(err.msgResp, { appearance: 'info', autoDismiss: true })
            history.push(`/group/join/${eventDetail?.group?.id || eventDetail?.groupId}`)
          }

          reportApi.report({
            message: `[Event details 04]${err.msgCode} - [Event detail] #onCreateOrder ${err.msgResp}`,
          })
          setError(err.msgResp)
        }
      }
    } catch (err) {
      addToast(err?.msgResp || 'Something went wrong1!', { appearance: 'error', autoDismiss: true })
      setIsSubmitted(false)
    }
  }

  const onSubmit = async () => {
    try {
      setError('')

      if (userInGroup !== undefined && userInGroup.banned === 1) {
        addToast('You can not order!', { appearance: 'error', autoDismiss: true })
        setError('Please check the note')
        return
      }
      setError('')

      if (eventDetail.bookingAddressRequired) {
        if (!addressBook.userAddresses || addressBook.userAddresses.length === 0) {
          addressBook.handleOpenAddNewAddress()
        } else {
          addressBook.toggleChangeAddressModal()
        }
      } else {
        await proceedWithOrder()
      }
    } catch (err) {
      addToast(err?.msgResp || 'Something went wrong! 5', { appearance: 'error', autoDismiss: true })
      setIsSubmitted(false)
    }
  }

  const handleAddressAdded = async (newAddressObj) => {
    try {
      if (newAddressObj && typeof newAddressObj === 'object') {
        addressBook.setSelectedDeliveryAddress(newAddressObj)
        await proceedWithOrder(newAddressObj)
        setTimeout(() => addressBook.refreshAddresses(), 1000)
      } else {
        const newAddresses = await addressBook.refreshAddresses()
        if (newAddresses.length > 0) {
          const newestAddress = newAddresses[newAddresses.length - 1]
          addressBook.setSelectedDeliveryAddress(newestAddress)
          await proceedWithOrder(newestAddress)
        }
      }
    } catch (err) {
      addToast('Failed to process new address', { appearance: 'error', autoDismiss: true })
    }
  }

  const handleAddressSelectedAndProceed = async (address) => {
    try {
      addressBook.setSelectedDeliveryAddress(address)
      await proceedWithOrder(address)
    } catch (err) {
      addToast(err?.msgResp || 'Something went wrong! 6', { appearance: 'error', autoDismiss: true })
      setIsSubmitted(false)
    }
  }

  const handleAddressAddedAndProceed = async (newAddressObj) => {
    try {
      if (newAddressObj && typeof newAddressObj === 'object') {
        addressBook.setSelectedDeliveryAddress(newAddressObj)
        await proceedWithOrder(newAddressObj)
        setTimeout(() => addressBook.refreshAddresses(), 1000)
      } else {
        const newAddresses = await addressBook.refreshAddresses()
        if (newAddresses.length > 0) {
          const newestAddress = newAddresses[newAddresses.length - 1]
          addressBook.setSelectedDeliveryAddress(newestAddress)
          await proceedWithOrder(newestAddress)
        }
      }
    } catch (err) {
      addToast('Failed to process new address', { appearance: 'error', autoDismiss: true })
    }
  }

  return {
    comment,
    setComment,
    error,
    setError,
    isPurchase,
    setIsPurchase,
    phoneOrEmail,
    setPhoneOrEmail,
    isSubmitted,
    setIsSubmitted,
    showModal,
    orderNo,
    hasItemWithAckRequired,
    proceedWithOrder,
    onSubmit,
    handleAddressAdded,
    handleAddressSelectedAndProceed,
    handleAddressAddedAndProceed,
  }
}

export default useOrderSubmission
